import { mapActions, mapGetters, mapState } from 'vuex';
import { AUCTION_TYPES } from '../constants';
import { subscribeToChannel } from '@js/utilities/pusher.js';

export const basePusher = {
  data() {
    return {
      pusherItemChannels: [],
      pusherTempItemChannel: null,
      pusherChannelName: jsConfig.pusher?.channelName,
      pusherOutBidEventChannel: jsConfig.pusher?.eventOutBidChannel,
      pusherBidEvent: jsConfig.pusher?.bidEvent,
      pusherWindowCloseEvent: jsConfig.pusher?.windowCloseEvent,
    }
  },
  computed: {
    ...mapGetters(['getItem']),
    ...mapState(['auctionType', 'items', 'tempItem', 'temporaryBidItems'])
  },
  methods: {
    ...mapActions(['addItemsToRefreshQueue', 'SET_STATE', 'UPDATE_ITEM']),
    bindToPusherItemChannels(pusherInstance) {
      this.items.forEach(({id}) => {
        if (this.getItemChannelName(id)) {
          const channel = subscribeToChannel(pusherInstance, this.getItemChannelName(id));
          channel.bind(this.pusherBidEvent, this.pusherUpdateItem);
          this.pusherItemChannels.push(channel);
        }
      });
    },
    unbindPusherItemChannels() {
      this.pusherItemChannels.forEach((channel) => {
        channel.unbind(this.pusherBidEvent, this.pusherUpdateItem);
      });
      this.pusherItemChannels = [];
    },
    getItemChannelName(itemId) {
      if (this.auctionType === AUCTION_TYPES.WEEKLY) {
        return `auction-listing-${itemId}`;
      } else if (this.auctionType === AUCTION_TYPES.EXTENDED) {
        return `extended-listing-${itemId}`;
      } else {
        return null;
      }
    },
    pusherWindowEventHelper(channel) {
      channel?.bind(this.pusherWindowCloseEvent, this.onPusherWindowEvent);
    },
    pusherWindowEventCleanup(channel) {
      channel?.unbind(this.pusherWindowCloseEvent, this.onPusherWindowEvent);
    },
    onPusherWindowEvent({ window: { id: windowId } }) {
      this.SET_STATE([{ stateName: 'currentWindowOrdinal', data: windowId + 1 }]);
    },
    pusherUpdateItem({ item: { current_bid, id: itemId, bid_count }, window: { id: windowId } = {} }) {
      const foundItem = this.getItem(itemId);
      if (!foundItem) {
        return;
      }
      this.$root.$emit('updateTimer');

      const { canVisitorBid, lastWindowIdToReceiveBid = null } = foundItem;

      if (canVisitorBid) {
        this.addItemsToRefreshQueue([itemId]);
      } else if (current_bid) {
        const updatedItem = {
          id: parseInt(itemId, 10),
          currentBid: current_bid,
          bidCount: bid_count
        };

        if (this.auctionType === AUCTION_TYPES.WEEKLY) {
          if (windowId && (windowId > (lastWindowIdToReceiveBid || 0))) {
            updatedItem.lastWindowIdToReceiveBid = windowId;
          }
        }

        this.UPDATE_ITEM(updatedItem);
      }
    },
  },
  mounted() {
    if (this.$root.pusherChannel) {
      this.pusherWindowEventHelper(this.$root.pusherChannel);
    }
    if (this.$root.pusherInstance) {
      this.bindToPusherItemChannels(this.$root.pusherInstance);
    }
  },
  beforeDestroy() {
    if (this.$root.pusherChannel) {
      this.pusherWindowEventCleanup(this.$root.pusherChannel);
    }
    if (this.$root.pusherInstance) {
      this.unbindPusherItemChannels();
    }
  },
  watch: {
    '$root.pusherChannel'(newChannel, oldChannel) {
      this.pusherWindowEventCleanup(oldChannel);
      this.pusherWindowEventHelper(newChannel);
    },
    '$root.pusherInstance'(newInstance) {
      this.unbindPusherItemChannels();
      this.bindToPusherItemChannels(newInstance);
    },
    'tempItem'(newItem, oldItem) {
      if (!newItem) {
        this.pusherTempItemChannel?.unbind(this.pusherBidEvent, this.pusherUpdateItem);
      } else if ((newItem && !oldItem) || ((newItem && oldItem) && (newItem.id !== oldItem.id))) {
        const channel = subscribeToChannel(this.$root.pusherInstance, this.getItemChannelName(newItem.id));

        if (channel) {
          channel.bind(this.pusherBidEvent, this.pusherUpdateItem);
          this.pusherTempItemChannel = channel;
        }
      }
    },
    'temporaryBidItems'(newItems, oldItems) {
      //loop through new items, if the item is not in the normal items array and also not in the old temp bid items, then subscribe to that item's channel
      newItems.forEach(newItem => {
        const newItemIsInItems = !!this.items.find(i => {
          return i.id === newItem.id;
        });
        const newItemIsInOldBidItems = !!oldItems.find(i => {
          return i.id === newItem.id;
        })
        if (!newItemIsInItems && !newItemIsInOldBidItems) {
          const channel = subscribeToChannel(this.$root.pusherInstance, this.getItemChannelName(newItem.id));
          channel.bind(this.pusherBidEvent, this.pusherUpdateItem);
          this.pusherItemChannels.push(channel);
        }
      });

      // loop through old items and if an item is no longer in the temp bid items and not in normal items, then unsubscribe
      oldItems.forEach(oldItem => {
        const oldItemIsInItems = !!this.items.find(i => {
          return i.id === oldItem.id;
        });
        const oldItemIsInNewBidItems = !!newItems.find(i => {
          return i.id === oldItem.id;
        })
        if (!oldItemIsInNewBidItems && !oldItemIsInItems) {
          const channelIndex = this.pusherItemChannels.findIndex(c => {
            return c.name === this.getItemChannelName(oldItem.id);
          });

          if (channelIndex !== -1) {
            const channel = this.pusherItemChannels[channelIndex];
            channel.unbind(this.pusherBidEvent, this.pusherUpdateItem);
            this.pusherItemChannels.splice(channelIndex, 1);
          }
        }
      });
    }
  }
}
